import type { ComponentPropsWithoutRef, MutableRefObject, RefObject } from "react";
import { forwardRef, useMemo } from "react";
import styled from "@emotion/styled";

import Input from "components/ui/input/Input";
import { InputGroup, InputGroupAddon } from "components/ui/input_group/InputGroup";
import UIcon from "lib/icons/untitled_ui/UIcon";
import type { GroupType, Option, OptionValue } from "./types";
import { flattenItems } from "./utils";

export type ComboboxInputProps = Omit<ComponentPropsWithoutRef<typeof Input>, "name" | "value"> & {
  placeholder?: string;
  name?: string;
  filterValue: string;
  setFilterValue: (value: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  value: OptionValue[];
  items: Option[] | GroupType[];
  multiple: boolean;
  inputRef: RefObject<HTMLDivElement>;
  inputElementRef?: MutableRefObject<HTMLInputElement | null>;
};

export const ComboboxInput = forwardRef<HTMLInputElement, ComboboxInputProps>(
  (
    {
      placeholder,
      name = "Select Search",
      filterValue,
      setFilterValue,
      open,
      setOpen,
      value,
      items,
      multiple,
      inputRef,
      inputElementRef,
      ...props
    },
    ref,
  ) => {
    const displayValue = useMemo(() => {
      if (filterValue) return filterValue;
      if (!multiple && value.length === 1) {
        // Find the option with this value and display its label
        const allOptions = flattenItems(items);
        const selectedOption = allOptions.find((opt) => opt.value === value[0]);
        return selectedOption?.label || "";
      }
      return "";
    }, [filterValue, value, items, multiple]);

    return (
      <StyledInputGroup
        ref={inputRef}
        onClick={() => {
          setOpen(true);
        }}
      >
        <InputGroupAddon align="inline-start">
          <UIcon icon="searchRefraction" color="sysTextSecondary" size={24} />
        </InputGroupAddon>
        {/* TODO - Add combineRefs from utils */}
        <StyledInput
          ref={(el) => {
            if (typeof ref === "function") ref(el);
            else if (ref) ref.current = el;
            if (inputElementRef) inputElementRef.current = el;
          }}
          data-slot="input-group-control"
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => {
            setFilterValue(e.target.value);
            if (!open) setOpen(true);
          }}
          name={name}
          {...props}
        />
      </StyledInputGroup>
    );
  },
);

ComboboxInput.displayName = "ComboboxInput";

const StyledInputGroup = styled(InputGroup)({
  borderRadius: "4px",
});

const StyledInput = styled(Input)({
  border: "none",
  outline: "none",
  "&:hover": {
    border: "none",
  },
  "&:focus": {
    border: "none",
    outline: "none",
  },
  height: 30,
});
